import React, { useEffect, useMemo, useState } from "react";
import { DatePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import { fetchAllWeekoff } from "../../../core/data/redux/weekoff/weekoff.Action";
import { fetchAllHolidays } from "../../../core/data/redux/holiday/holidayAction";
import { formatTimeOnly } from "../../../core/utils/formatTime";
import { getEmployees } from "../../../core/data/redux/employee/employeeAction";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

/* ------------------------------------------------------------------
   TYPES
------------------------------------------------------------------ */

interface PunchItem {
    PunchDate: string;
    PunchIn?: string;
    PunchOut?: string;
    Duration?: string;
}

interface Employee {
    EmployeeID: number;
    Name: string;
    EmployeeNumber?: string;
    DepartmentId?: number;
    BranchId?: number;
    BranchID?: number;
    PictureURL?: string;
}

interface HolidayItem {
    Id: number;
    Name: string;
    StartDate: string;
    EndDate: string;
    Status: string;
    HolidayType: string; // "fixed"
}

type WeekPattern = Record<
    string, // "Sunday", "Monday", ...
    {
        all?: boolean;
        weeks?: Record<1 | 2 | 3 | 4 | 5, boolean>;
    }
>;

interface WeekoffItem {
    Status: string;
    Type: "employee" | "department" | "branch";
    AppliedTo: string | number[] | null;
    WeekPattern: WeekPattern | string | null;
}

interface ReduxState {
    holiday: { holidays: HolidayItem[] };
    weekoff: { weekoff: WeekoffItem[] };
    employee: { employees: Employee[] };
}

interface CalendarDay {
    date: Date | null;
    isCurrent: boolean;
    status: "present" | "holiday" | "weekly-off" | "absent" | "future";
    attendance?: PunchItem | null;
    holidayName?: string | null;
}

interface Props {
    punchData: PunchItem[];
    employeeId: number;
    onMonthChange?: (month: Dayjs) => void;
}

/* ------------------------------------------------------------------
   WEEK-OFF HELPERS
------------------------------------------------------------------ */

const normalizePattern = (raw: any): WeekPattern => {
    if (!raw) return {};
    if (typeof raw === "string") {
        try {
            return JSON.parse(raw);
        } catch {
            return {};
        }
    }
    return raw;
};

const toIdArray = (raw: any): number[] => {
    if (!raw) return [];
    try {
        const arr = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (Array.isArray(arr)) return arr.map(Number);
    } catch { }
    return [Number(raw)];
};

const getWeekIndex = (d: Dayjs): 1 | 2 | 3 | 4 | 5 => {
    return Math.ceil(d.date() / 7) as 1 | 2 | 3 | 4 | 5;
};

/* ------------------------------------------------------------------
   MAIN COMPONENT — TSX VERSION
------------------------------------------------------------------ */

const EmployeeCalendar: React.FC<Props> = ({ punchData, employeeId, onMonthChange = () => { }, }) => {
    const dispatch = useDispatch<AppDispatch>();

    const { holidays } = useSelector((s: ReduxState) => s.holiday);
    const { weekoff } = useSelector((s: ReduxState) => s.weekoff);
    const { employees } = useSelector((s: ReduxState) => s.employee);

    const employee = employees.find((e) => Number(e.EmployeeID) === Number(employeeId));

    const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());
    const [calendarDays, setCalendarDays] = useState<CalendarDay[]>([]);

    const today = dayjs().format("YYYY-MM-DD");

    /* ------------------------------------------------------------------
       LOAD DATA
    ------------------------------------------------------------------ */
    useEffect(() => {
        dispatch(fetchAllHolidays());
        dispatch(fetchAllWeekoff());
    }, [dispatch]);

    useEffect(() => {
        if (!employees.length) {
            dispatch(getEmployees());
        }
    }, [employees.length, dispatch]);


    useEffect(() => {
        generateCalendar(selectedMonth);
    }, [punchData, holidays, weekoff, employee]);

    /* ------------------------------------------------------------------
       FIXED HOLIDAY MAP
    ------------------------------------------------------------------ */
    const fixedHolidayMap = useMemo(() => {
        const map = new Map<string, string>();

        holidays.forEach((h) => {
            if (!h?.Status) return;
            if (h.Status.toLowerCase() !== "active") return;

            const type = (h.HolidayType || "").toLowerCase();

            // 🎯 Mark both Fixed & National_Holiday as fixed holidays
            if (type !== "fixed" && type !== "national_holiday") return;

            let start = dayjs(h.StartDate);
            let end = dayjs(h.EndDate);

            while (start.isSameOrBefore(end, "day")) {
                map.set(start.format("YYYY-MM-DD"), h.Name || "Holiday");
                start = start.add(1, "day");
            }
        });

        return map;
    }, [holidays]);
    console.log({ fixedHolidayMap })

    /* ------------------------------------------------------------------
       WEEKLY OFF MAP (Employee → dayName → Set<weekIndex>)
    ------------------------------------------------------------------ */
    const weeklyOffMap = useMemo(() => {
        if (!employee) return new Map<string, Set<number>>();

        const empWeekly = new Map<number, Map<string, Set<number>>>();
        const deptWeekly = new Map<number, Map<string, Set<number>>>();
        const branchWeekly = new Map<number, Map<string, Set<number>>>();

        const pushPattern = (
            targetMap: Map<number, Map<string, Set<number>>>,
            id: number,
            pattern: WeekPattern
        ) => {
            const entry = targetMap.get(id) ?? new Map<string, Set<number>>();

            Object.entries(pattern).forEach(([day, rules]) => {
                const set = entry.get(day) ?? new Set<number>();

                if (rules.all) {
                    [1, 2, 3, 4, 5].forEach((w) => set.add(w));
                } else if (rules.weeks) {
                    Object.entries(rules.weeks).forEach(([week, val]) => {
                        if (val) set.add(Number(week));
                    });
                }

                entry.set(day, set);
            });

            targetMap.set(id, entry);
        };

        weekoff.forEach((wf) => {
            if (wf.Status.toLowerCase() !== "active") return;

            const ids = toIdArray(wf.AppliedTo);
            const pattern = normalizePattern(wf.WeekPattern);
            const type = wf.Type.toLowerCase();

            if (type === "employee") ids.forEach((id) => pushPattern(empWeekly, id, pattern));
            if (type === "department") ids.forEach((id) => pushPattern(deptWeekly, id, pattern));
            if (type === "branch") ids.forEach((id) => pushPattern(branchWeekly, id, pattern));
        });

        const empId = Number(employee.EmployeeID);
        if (empWeekly.get(empId)) return empWeekly.get(empId)!;

        const deptId = Number(employee.DepartmentId);
        if (deptWeekly.get(deptId)) return deptWeekly.get(deptId)!;

        const branchId = Number(employee.BranchId ?? employee.BranchID);
        if (branchWeekly.get(branchId)) return branchWeekly.get(branchId)!;

        return new Map<string, Set<number>>();
    }, [weekoff, employee]);

    /* ------------------------------------------------------------------
       BUILD CALENDAR
    ------------------------------------------------------------------ */
    const generateCalendar = (monthDate: Date) => {
        if (!employee) return;

        const year = monthDate.getFullYear();
        const month = monthDate.getMonth();

        const first = dayjs(`${year}-${month + 1}-01`);
        const last = first.endOf("month");

        const startDay = first.day();  // which weekday month starts on
        const totalDays = last.date(); // number of days in month

        const attendanceMap: Record<string, PunchItem> = {};
        punchData.forEach((p) => {
            attendanceMap[dayjs(p.PunchDate).format("YYYY-MM-DD")] = p;
        });

        const days: CalendarDay[] = [];

        /* --------------------------------------------------------
           1) PREVIOUS MONTH DAYS (disabled cells)
        -------------------------------------------------------- */
        if (startDay > 0) {
            const prevMonthLast = first.subtract(1, "month").endOf("month");

            for (let i = startDay - 1; i >= 0; i--) {
                const dateObj = prevMonthLast.subtract(i, "day");

                days.push({
                    date: dateObj.toDate(),
                    isCurrent: false,
                    status: "future", // not used, but required
                });
            }
        }

        /* --------------------------------------------------------
           2) CURRENT MONTH DAYS (real logic)
        -------------------------------------------------------- */
        for (let d = 1; d <= totalDays; d++) {
            const dateObj = dayjs(`${year}-${month + 1}-${d}`);
            const dateStr = dateObj.format("YYYY-MM-DD");

            const attendance = attendanceMap[dateStr];
            let status: CalendarDay["status"] = "absent";

            if (attendance) status = "present";
            else if (fixedHolidayMap.has(dateStr)) status = "holiday";
            else {
                const dayName = dateObj.format("dddd");
                const weekIndex = getWeekIndex(dateObj);
                const isWeekOff = weeklyOffMap.get(dayName)?.has(weekIndex);

                if (isWeekOff) status = "weekly-off";
                else if (dateStr > today) status = "future";
            }

            days.push({
                date: dateObj.toDate(),
                isCurrent: true,
                status,
                attendance,
                holidayName: fixedHolidayMap.get(dateStr) || null,
            });
        }

        /* --------------------------------------------------------
           3) NEXT MONTH DAYS (to complete full 6 rows)
        -------------------------------------------------------- */
        const totalCells = days.length;
        const remaining = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);

        if (remaining > 0) {
            const nextStart = last.add(1, "day");

            for (let i = 0; i < remaining; i++) {
                const nextDate = nextStart.add(i, "day");

                days.push({
                    date: nextDate.toDate(),
                    isCurrent: false,
                    status: "future",
                });
            }
        }

        setCalendarDays(days);
    };


    /* ------------------------------------------------------------------
       RENDER
    ------------------------------------------------------------------ */

    return (
        <div className="calendar-wrapper">
            <div className="calendar-controls">
                <h6>📅 Attendance Calendar</h6>
                <DatePicker
                    picker="month"
                    allowClear={false}
                    value={dayjs(selectedMonth)}
                    format="MMMM YYYY"
                    onChange={(v) => {
                        if (!v) return;
                        setSelectedMonth(v.toDate());
                        generateCalendar(v.toDate());

                        // 🔥 Call parent AttendanceManager to fetch fresh data
                        onMonthChange?.(v);
                    }}

                />
            </div>

            <div className="calendar-grid">
                {/* Header */}
                <div className="calendar-header">
                    {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                        <div key={d}>{d}</div>
                    ))}
                </div>

                {/* Day Cells */}
                <div className="calendar-body">
                    {calendarDays.map((day, idx) => {
                        if (!day.isCurrent)
                            return (
                                <div key={idx} className="calendar-cell disabled">
                                    <div className="day-number disabled-day">
                                        {dayjs(day.date!).date()}
                                    </div>
                                </div>
                            );


                        const num = dayjs(day.date!).date();

                        return (
                            <div
                                key={idx}
                                className={`
                                calendar-cell
                                ${day.status === "present" ? "attendance-present" : ""}
                                ${day.status === "absent" ? "attendance-absent" : ""}
                                ${day.status === "future" ? "attendance-future" : ""}
                                ${day.status === "holiday" ? "attendance-holiday" : ""}
                                ${day.status === "weekly-off" ? "attendance-weekly-off" : ""}
                                `}
                                style={{
                                    background:
                                        day.status === "holiday"
                                            ? "#fff2f0"
                                            : day.status === "weekly-off"
                                                ? "#f6ffed"
                                                : undefined,
                                }}
                            >
                                <div className="day-number">{num}</div>

                                {day.status === "holiday" && (
                                    <div className="att-holiday">{day.holidayName}</div>
                                )}

                                {day.status === "weekly-off" && (
                                    <div className="att-weekoff">Weekly Off</div>
                                )}

                                {day.status === "present" && day.attendance && (
                                    <div className="att-details">
                                        <div>
                                            <i className="ti ti-login" />{" "}
                                            <strong>
                                                {day.attendance.PunchIn
                                                    ? formatTimeOnly(day.attendance.PunchIn)
                                                    : "--"}
                                            </strong>
                                        </div>
                                        <div>
                                            <strong>
                                                <i className="ti ti-logout" />{" "}
                                                {day.attendance.PunchOut
                                                    ? formatTimeOnly(day.attendance.PunchOut)
                                                    : "--"}
                                            </strong>
                                        </div>
                                        <div>
                                            <strong>
                                                <i className="ti ti-clock-hour-4" />{" "}
                                                {day.attendance.Duration || "--"}
                                            </strong>
                                        </div>
                                    </div>
                                )}

                                {day.status === "absent" && (
                                    <div className="att-absent">Absent</div>
                                )}

                                {day.status === "future" && (
                                    <div className="att-future">Upcoming</div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default EmployeeCalendar;
